import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
} from "rxjs";
import { UserPermissionsService } from "./common/user-permissions.service";
import { RoutesService } from "./routes.service";
import { NextPageRoute } from "@cvx/nextpage";
import { GroupMembershipCacheService } from "./group-membership-cache.service";
import { GroupName, GroupUtils } from "../utils/group-names";

/**
 * Service that manages route permissions and automatically refreshes
 * the navigation when user permissions change
 */
@Injectable({
  providedIn: "root",
})
export class RoutePermissionService {
  private originalRoutes: NextPageRoute[] = [];
  private _permissionsLoaded = new BehaviorSubject<boolean>(false);

  public permissionsLoaded$ = this._permissionsLoaded.asObservable();
  constructor(
    private userPermissionsService: UserPermissionsService,
    private routesService: RoutesService,
    private groupMembershipCache: GroupMembershipCacheService
  ) {
    this.initializePermissionWatcher();
  }
  /**
   * Initialize the original routes and start watching for permission changes
   * @param routes - The original unfiltered routes configuration
   */ public initializeRoutes(routes: NextPageRoute[]): void {
    this.originalRoutes = [...routes]; // Extract all individual group requirements and preload them for better performance
    const allIndividualGroups = this.extractGroupRequirements(routes);
    if (allIndividualGroups.length > 0) {
      this.groupMembershipCache
        .preloadIndividualGroupMemberships(allIndividualGroups)
        .subscribe({
          next: () => {
            this.proceedWithRouteInitialization();
          },
          error: (error) => {
            console.error(
              "Error preloading individual group memberships:",
              error
            );
            // Proceed anyway, individual checks will happen as needed
            this.proceedWithRouteInitialization();
          },
        });
    } else {
      this.proceedWithRouteInitialization();
    }
  }

  /**
   * Continue with route initialization after group memberships are preloaded
   */
  private proceedWithRouteInitialization(): void {
    // Load user permissions first
    this.userPermissionsService.loadUserPermissions();

    // Wait for permissions to load, then filter routes
    this.userPermissionsService.isLoading$
      .pipe(distinctUntilChanged(), debounceTime(100))
      .subscribe((isLoading) => {
        if (!isLoading) {
          // Permissions are loaded, now filter and set up routes
          this.routesService.filterRoutesForMenu(this.originalRoutes);
          this._permissionsLoaded.next(true);
        }
      });
  }
  /**
   * Extract all unique individual group requirements from routes and their children
   */
  private extractGroupRequirements(routes: NextPageRoute[]): string[] {
    const groups = new Set<string>();

    const extractFromRoute = (route: NextPageRoute) => {
      const groupAccessData = route.data as any;
      if (groupAccessData?.requiredGroups?.length) {
        groupAccessData.requiredGroups.forEach((groupName: GroupName) => {
          // Extract individual groups from comma-separated GroupName constants
          const individualGroups = GroupUtils.getIndividualGroups(groupName);
          individualGroups.forEach((group) => groups.add(group));
        });
      }

      if (route.children?.length) {
        route.children.forEach(extractFromRoute);
      }
    };

    routes.forEach(extractFromRoute);
    return Array.from(groups);
  }

  /**
   * Watch for permission changes and refresh routes accordingly
   */
  private initializePermissionWatcher(): void {
    // Watch for changes in user permissions
    this.userPermissionsService.permissions$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          // Only refresh if relevant permissions actually changed
          if (!prev || !curr) return false;

          return (
            prev.canAccessTurbines === curr.canAccessTurbines &&
            prev.canAccessRamTargets === curr.canAccessRamTargets &&
            prev.canViewTechnicalLetters === curr.canViewTechnicalLetters &&
            prev.canAdminTechnicalLetters === curr.canAdminTechnicalLetters
          );
        }),
        debounceTime(200) // Debounce to avoid excessive updates
      )
      .subscribe((permissions) => {
        if (permissions && this.originalRoutes.length > 0) {
          // Clear cache when permissions change to ensure fresh data
          this.groupMembershipCache.clearCache();
          this.routesService.refreshFilteredRoutes(this.originalRoutes);
        }
      });
  }

  /**
   * Manually refresh the routes (useful for testing or manual triggers)
   */
  public refreshRoutes(): void {
    if (this.originalRoutes.length > 0) {
      this.routesService.refreshFilteredRoutes(this.originalRoutes);
    }
  }

  /**
   * Get the current filtered routes
   */
  public getFilteredRoutes(): NextPageRoute[] {
    return this.routesService.getFilteredRoutes();
  }

  /**
   * Check if routes have been loaded and filtered
   */
  public areRoutesReady(): boolean {
    return (
      this._permissionsLoaded.value &&
      this.routesService.filteredRoutesLoaded.value
    );
  }

  /**
   * Get cache statistics for debugging and monitoring
   */
  public getCacheStats(): any {
    return this.groupMembershipCache.getCacheStats();
  }

  /**
   * Clear the group membership cache (useful for testing or when user context changes)
   */
  public clearCache(): void {
    this.groupMembershipCache.clearCache();
  }
}
